import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tokensApi, type FreepassToken } from '../../api/tokens.js';
import { Spinner, ErrorBox, EmptyBox } from '../../components/ui.js';
import { datetime } from '../../lib/format.js';

/**
 * 성인인증 프리패스 토큰 발급.
 *
 * 원본: custom/make_token.php
 * 버튼 하나로 토큰을 만들고, 그 토큰이 붙은 링크(PC·모바일)를 준다.
 * 원본에 없던 발급 내역을 아래에 함께 둔다 - 무엇이 살아 있는지 보여야 한다.
 */

export function FreepassPage() {
  const qc = useQueryClient();
  const [copied, setCopied] = useState<string | null>(null);

  const history = useQuery({
    queryKey: ['tokens', 'freepass'],
    queryFn: () => tokensApi.listFreepass(),
  });

  const issue = useMutation({
    mutationFn: () => tokensApi.issueFreepass(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens', 'freepass'] }),
  });

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // 클립보드를 못 쓰는 환경이면 사용자가 직접 고르면 된다 (입력칸은 그대로 있다).
    }
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            성인인증 프리패스 토큰
            <span className="badge badge--danger">관리자 전용</span>
          </h1>
          <p className="page-desc">
            발급한 링크를 받은 사람은 <strong>3시간 동안</strong> 성인인증을 건너뜁니다.
            필요한 사람에게만 보내주세요.
          </p>
        </div>
      </header>

      <div className="panel">
        <button className="btn" type="button" onClick={() => issue.mutate()} disabled={issue.isPending}>
          {issue.isPending ? '발급 중…' : '토큰 발급'}
        </button>

        {issue.isError && <ErrorBox error={issue.error} />}

        {issue.data && (
          <div className="token-result">
            <p className="panel__note">
              만료: <strong>{datetime(issue.data.expiresAt)}</strong>
            </p>
            {(
              [
                ['PC 링크 (www)', issue.data.pcUrl],
                ['모바일 링크 (m)', issue.data.mobileUrl],
              ] as const
            ).map(([label, url]) => (
              <div className="token-url" key={label}>
                <span className="token-url__label">{label}</span>
                <input className="input" readOnly value={url} onFocus={(e) => e.target.select()} />
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => void copy(url)}>
                  {copied === url ? '복사됨' : '복사'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">최근 발급 내역</h2>
        {history.isPending ? (
          <Spinner />
        ) : history.isError ? (
          <ErrorBox error={history.error} onRetry={() => void history.refetch()} />
        ) : history.data.length === 0 ? (
          <EmptyBox>발급한 토큰이 없습니다.</EmptyBox>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>토큰</th>
                  <th>발급</th>
                  <th>만료</th>
                  <th style={{ textAlign: 'center' }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((t: FreepassToken) => (
                  <tr key={t.id}>
                    <td className="token-cell">{t.token}</td>
                    <td className="td-muted">{datetime(t.createdAt)}</td>
                    <td className="td-muted">{datetime(t.expiresAt)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {t.alive ? (
                        <span className="badge badge--warn">유효</span>
                      ) : (
                        <span className="td-muted">만료</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* 만료된 토큰은 앞자리만 보여준다 - 목록을 보는 것만으로 쓸 수 있으면 안 된다. */}
        <p className="panel__note">만료된 토큰은 앞 네 자리만 표시합니다.</p>
      </div>
    </section>
  );
}
